import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { celebrateBadges } from "../lib/gamification";
import {
  MessageCircleQuestion, Send, Sparkles, Plus, Trash2,
  Lightbulb, PenLine, RefreshCw, NotebookPen,
} from "lucide-react";

const PHASES = [
  { id: "brainstorm", label: "Brainstorm", icon: Lightbulb, color: "amber" },
  { id: "draft", label: "Draft", icon: PenLine, color: "indigo" },
  { id: "revise", label: "Revise", icon: RefreshCw, color: "rose" },
  { id: "reflect", label: "Reflect", icon: NotebookPen, color: "emerald" },
];

const phaseMeta = (id) => PHASES.find((p) => p.id === id) || PHASES[0];

export default function WritingConference() {
  const [conferences, setConferences] = useState([]);
  const [active, setActive] = useState(null); // {conference, messages}
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newForm, setNewForm] = useState({ title: "", writing_focus: "" });
  const [showNew, setShowNew] = useState(false);
  const endRef = useRef(null);

  const loadList = async () => {
    try {
      const { data } = await api.get("/conference/list");
      setConferences(data.conferences || []);
    } catch { /* noop */ }
  };

  useEffect(() => { loadList(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [active?.messages]);

  const openConference = async (id) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/conference/${id}`);
      setActive(data);
    } catch { toast.error("Could not load conference"); }
    finally { setLoading(false); }
  };

  const createConference = async () => {
    if (!newForm.title.trim()) { toast.error("Give your conference a title"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/conference/create", newForm);
      setShowNew(false);
      setNewForm({ title: "", writing_focus: "" });
      await loadList();
      await openConference(data.id);
      toast.success("Conference started");
    } catch { toast.error("Could not start conference"); }
    finally { setCreating(false); }
  };

  const sendMessage = async () => {
    if (!input.trim() || !active) return;
    const text = input.trim();
    setInput("");
    const studentMsg = { role: "student", text, ts: new Date().toISOString() };
    setActive((s) => ({ ...s, messages: [...s.messages, studentMsg] }));
    setLoading(true);
    try {
      const { data } = await api.post(`/conference/${active.conference.id}/message`, { text });
      const coachMsg = { role: "coach", text: data.reply, ts: new Date().toISOString() };
      setActive((s) => ({
        ...s,
        messages: [...s.messages, coachMsg],
        conference: { ...s.conference, phase: data.phase, message_count: (s.conference.message_count || 0) + 2 },
      }));
      if (data.phase_advanced) toast.success(`Phase: ${data.phase}`);
      celebrateBadges(data._meta);
      loadList();
    } catch (e) {
      if (e.response?.status === 402) toast.error(e.response.data.detail.message);
      else toast.error("Coach is offline");
    } finally { setLoading(false); }
  };

  const deleteConference = async (id) => {
    if (!window.confirm("Delete this conference and its messages?")) return;
    try {
      await api.delete(`/conference/${id}`);
      if (active?.conference?.id === id) setActive(null);
      await loadList();
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div className="space-y-6" data-testid="conference-page">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-2xl bg-violet-100 text-violet-700 grid place-items-center">
            <MessageCircleQuestion size={20} />
          </div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "Outfit" }}>Writing Conference</h1>
        </div>
        <p className="text-slate-600">Sit down with Mr. Ramsey for a real one-on-one. Brainstorm → Draft → Revise → Reflect.</p>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        {/* Conferences list */}
        <aside className="space-y-3">
          <button
            data-testid="new-conference-btn"
            onClick={() => setShowNew((s) => !s)}
            className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-2xl px-4 py-3 inline-flex items-center justify-center gap-2"
            style={{ boxShadow: "0 4px 0 0 rgb(67 56 202)" }}
          >
            <Plus size={16} /> New conference
          </button>

          {showNew && (
            <div className="tactile-card p-4 space-y-3" data-testid="new-conference-form">
              <input
                data-testid="new-conf-title"
                placeholder="Title (e.g. 'My SAT practice essay')"
                value={newForm.title}
                onChange={(e) => setNewForm({ ...newForm, title: e.target.value })}
                className="w-full rounded-xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-violet-400 px-3 py-2 outline-none text-sm"
              />
              <input
                data-testid="new-conf-focus"
                placeholder="Writing focus (optional)"
                value={newForm.writing_focus}
                onChange={(e) => setNewForm({ ...newForm, writing_focus: e.target.value })}
                className="w-full rounded-xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-violet-400 px-3 py-2 outline-none text-sm"
              />
              <button
                data-testid="new-conf-create"
                disabled={creating}
                onClick={createConference}
                className="w-full bg-violet-600 hover:bg-violet-500 text-white font-bold rounded-xl py-2 text-sm disabled:opacity-60"
              >
                {creating ? "Starting…" : "Start coaching"}
              </button>
            </div>
          )}

          <div className="space-y-2">
            {conferences.length === 0 && (
              <p className="text-xs text-slate-500 px-1">No conferences yet — start your first one above.</p>
            )}
            {conferences.map((c) => {
              const pm = phaseMeta(c.phase);
              const Icon = pm.icon;
              const isActive = active?.conference?.id === c.id;
              return (
                <button
                  key={c.id}
                  data-testid={`conf-item-${c.id}`}
                  onClick={() => openConference(c.id)}
                  className={`w-full text-left rounded-2xl px-4 py-3 border-2 transition group ${
                    isActive ? "bg-violet-600 text-white border-violet-700" : "bg-white border-slate-100 hover:border-violet-300"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-sm truncate">{c.title}</div>
                      <div className={`text-xs mt-1 inline-flex items-center gap-1 ${isActive ? "text-violet-100" : `text-${pm.color}-600`}`}>
                        <Icon size={11} /> {pm.label} · {c.message_count || 0} msgs
                      </div>
                    </div>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); deleteConference(c.id); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); deleteConference(c.id); } }}
                      data-testid={`conf-delete-${c.id}`}
                      className={`opacity-0 group-hover:opacity-100 p-1 rounded ${isActive ? "text-violet-100 hover:text-white" : "text-slate-400 hover:text-rose-500"}`}
                    >
                      <Trash2 size={14} />
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Conversation */}
        <section className="tactile-card p-0 overflow-hidden min-h-[60vh] flex flex-col">
          {!active ? (
            <div className="flex-1 grid place-items-center p-10 text-center">
              <div>
                <Sparkles size={32} className="mx-auto text-violet-400 mb-3" />
                <h3 className="font-bold text-lg mb-1" style={{ fontFamily: "Outfit" }}>
                  Pick a conference or start a new one
                </h3>
                <p className="text-sm text-slate-500 max-w-sm">
                  Each conference is a single piece of writing. You'll move through Brainstorm, Draft, Revise, and Reflect with the coach.
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between" data-testid="conf-header">
                <div className="min-w-0">
                  <h2 className="font-bold text-lg truncate" style={{ fontFamily: "Outfit" }}>
                    {active.conference.title}
                  </h2>
                  {active.conference.writing_focus && (
                    <p className="text-xs text-slate-500 truncate">{active.conference.writing_focus}</p>
                  )}
                </div>
                <div className="flex gap-1.5">
                  {PHASES.map((p) => {
                    const Icon = p.icon;
                    const isAt = active.conference.phase === p.id;
                    return (
                      <span
                        key={p.id}
                        data-testid={`phase-pill-${p.id}`}
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider border-2 ${
                          isAt ? `bg-${p.color}-100 text-${p.color}-700 border-${p.color}-300` : "bg-white text-slate-400 border-slate-100"
                        }`}
                      >
                        <Icon size={10} /> {p.label}
                      </span>
                    );
                  })}
                </div>
              </div>

              <div data-testid="conf-thread" className="flex-1 overflow-y-auto p-6 space-y-4">
                {active.messages.map((m, i) => (
                  <div key={i} className={`flex gap-3 ${m.role === "student" ? "flex-row-reverse" : ""}`}>
                    <div className={`w-9 h-9 rounded-full grid place-items-center flex-shrink-0 ${
                      m.role === "student" ? "bg-indigo-600 text-white" : "bg-violet-100 text-violet-700"
                    }`}>
                      {m.role === "student" ? <span className="text-xs font-bold">YOU</span> : <Sparkles size={16} />}
                    </div>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-3 whitespace-pre-wrap ${
                      m.role === "student" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-900"
                    }`}>
                      {m.text}
                    </div>
                  </div>
                ))}
                {loading && <div className="text-sm text-slate-500 italic">Mr. Ramsey is thinking…</div>}
                <div ref={endRef} />
              </div>

              <div className="border-t border-slate-100 p-3 flex gap-2">
                <input
                  data-testid="conf-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !loading && sendMessage()}
                  placeholder="Share where you're at…"
                  className="flex-1 rounded-full border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-violet-400 px-5 py-3 outline-none"
                />
                <button
                  data-testid="conf-send"
                  onClick={sendMessage}
                  disabled={loading || !input.trim()}
                  className="bg-violet-600 hover:bg-violet-500 text-white font-bold rounded-full px-5 py-3 inline-flex items-center gap-2 disabled:opacity-60"
                  style={{ boxShadow: "0 4px 0 0 rgb(91 33 182)" }}
                >
                  <Send size={16} />
                </button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
